import styled from "@emotion/styled";
import { UnsupportedBehavior } from "@shopify/react-idle";
import toNumber from "lodash/toNumber";
import type { FunctionComponent } from "react";
import { useEffect, useMemo, useState } from "react";

import { normalizeJsxAttribute, notEmpty } from "shared/lib/utils";

import { IdleQueue } from "scmp-app/components/common/idle-queue";
import { StaticSourceUrl } from "scmp-app/components/schema-render/common/iframe/enums";
import { getDimensionWithUnit } from "scmp-app/components/schema-render/common/iframe/helpers";
import type { ContentSchemaRenderProps } from "scmp-app/components/schema-render/content";

import { IframeAllowValue, VideoSourceUrl } from "./enums";
import type { StyledProps } from "./styles";
import { Container, StyledIframe, WrapperContainer } from "./styles";

export type Props = ContentSchemaRenderProps &
  ResponsiveAttributes & {
    allow?: string;
    allowfullscreen?: string;
    height?: string;
    id?: string;
    src?: string;
    style?: string;
    title?: string;
    width?: string;
  };

type IframeMessageData = {
  "datawrapper-height"?: Record<string, number>;
  "multimedia-height"?: Record<string, number>;
};

type ResponsiveAttributes = {
  ["data-mdheight"]?: string;
  ["data-responsive"]?: string;
  ["data-smheight"]?: string;
};

const defaultSize = "600";

export const Component: FunctionComponent<Props> = ({
  allow,
  allowfullscreen,
  height,
  schemaNode,
  src: source = "",
  style: _style,
  width,
  ...attribs
}) => {
  const iframeAllowFullScreen = allowfullscreen ? true : false;
  const iframeAllow =
    iframeAllowFullScreen && !notEmpty(allow) ? IframeAllowValue.Fullscreen : allow;

  const { height: iframeHeight = defaultSize, width: iframeWidth = defaultSize } =
    schemaNode.attribs ?? {};

  const justifyIframeSize = (value: string, fallback: string) => {
    if (value.endsWith("%") || value === "auto") return fallback;
    return value;
  };

  const isDataWrapperSource = source.includes(StaticSourceUrl.DataWrapper);
  const isFacebookSocialPluginSource = source.includes(StaticSourceUrl.FacebookSocialPlugin);
  const isSCMPMultimediaSource = source.includes(StaticSourceUrl.SCMPMultimedia);
  const isVideoSource = Object.values(VideoSourceUrl).some(url => source.includes(url));

  const parsedIframeWidth = Number.parseFloat(justifyIframeSize(iframeWidth, defaultSize));
  const parsedIframeHeight = Number.parseFloat(justifyIframeSize(iframeHeight, defaultSize));
  const messageIframeKey = attribs?.id ?? source;

  const [adjustedIframeHeight, setAdjustedIframeHeight] = useState(parsedIframeHeight);

  useEffect(() => {
    const handler = (event: MessageEvent<IframeMessageData>) => {
      if (
        !notEmpty(event?.data["datawrapper-height"]) &&
        !notEmpty(event?.data["multimedia-height"])
      )
        return;

      const key = messageIframeKey.replaceAll("datawrapper-chart-", "");
      const height =
        event?.data?.["datawrapper-height"]?.[key] ?? event?.data?.["multimedia-height"]?.[key];

      if (notEmpty(height)) {
        setAdjustedIframeHeight(height);
        return;
      }
    };

    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, [messageIframeKey]);

  const variant = useMemo<StyledProps["$variant"]>(() => {
    if (isDataWrapperSource) {
      return {
        $type: "datawrapper",
      };
    }

    const parsedWidth = toNumber(width);
    const parsedHeight = toNumber(height);

    if (isFacebookSocialPluginSource) {
      return {
        $height: parsedHeight,
        $type: "facebook",
        $width: parsedWidth,
      };
    }

    if (isSCMPMultimediaSource) {
      return {
        $blockSize: getDimensionWithUnit(adjustedIframeHeight ?? height),
        $type: "static",
      };
    }

    if (isVideoSource) {
      return {
        $aspectRatio: parsedWidth && parsedHeight ? parsedWidth / parsedHeight : 16 / 9,
        $type: "video",
      };
    }

    const isResponsive = attribs["data-responsive"] === "1";
    if (isResponsive) {
      return {
        $responsiveBlockSize: {
          desktopUp: getDimensionWithUnit(height),
          mobileUp: getDimensionWithUnit(
            attribs["data-smheight"] ?? attribs["data-mdheight"] ?? height,
          ),
          tabletUp: getDimensionWithUnit(attribs["data-mdheight"] ?? height),
        },
        $type: "responsive",
      };
    }

    if (!notEmpty(width) || !notEmpty(height))
      return { $aspectRatio: 16 / 9, $type: "aspectRatio" };

    // Prevent the case on width or height is 100%
    if (parsedWidth > 0 && parsedHeight > 0)
      return { $aspectRatio: parsedWidth / parsedHeight, $type: "aspectRatio" };

    return { $blockSize: getDimensionWithUnit(height), $type: "static" };
  }, [
    adjustedIframeHeight,
    attribs,
    height,
    isDataWrapperSource,
    isFacebookSocialPluginSource,
    isSCMPMultimediaSource,
    isVideoSource,
    width,
  ]);

  // Remove style from iframe to prevent not able to show infographic widget
  const { height: _1, style: _2, width: _3, ...schemaAttributes } = schemaNode.attribs ?? {};

  return (
    <WrapperContainer $variant={variant}>
      <Container $variant={variant}>
        <IdleQueue unsupportedBehavior={UnsupportedBehavior.AnimationFrame}>
          <StyledIframe
            $fitContainer={variant.$type !== "datawrapper" && variant.$type !== "multimedia"}
            allow={iframeAllow}
            allowFullScreen={iframeAllowFullScreen}
            src={source}
            {...normalizeJsxAttribute(schemaAttributes)}
            {...attribs}
            height={adjustedIframeHeight}
            width={parsedIframeWidth}
          />
        </IdleQueue>
      </Container>
    </WrapperContainer>
  );
};

Component.displayName = "GenericIframe";

export const GenericIframe = styled(Component)``;
